import { useState, useMemo } from 'react'
import { useAnalyticsContext } from 'AnalyticsDashboard/AnalyticsContext'
import { CHART_TYPES } from './QueryBuilder/chartTypes'
import { buildConfig, restoreSlots } from './QueryBuilder/buildConfig'
import { SlotEditor } from './QueryBuilder/SlotEditor'
import { Filters } from './Filters'

function generateName(kind, chartType, slotValues, queryInfo) {
  if (!kind || !chartType) return ''
  const chartDef = CHART_TYPES[chartType]
  if (!chartDef) return ''

  const parts = []

  // Data source name
  const sourceName = queryInfo?.name || _.capitalize(kind)
  parts.push(sourceName)

  // Metric names
  const rawMetrics = slotValues.metrics
  const metricIds = Array.isArray(rawMetrics) ? rawMetrics : rawMetrics ? [rawMetrics] : []
  if (metricIds.length > 0 && queryInfo) {
    const metricLabels = metricIds
      .map(id => queryInfo.availableMetrics?.find(m => m.id === id)?.label || id)
      .slice(0, 3)
    parts.push(metricLabels.join(', '))
  }

  // Primary dimension (first non-metric slot with a value)
  for (const [key, slot] of Object.entries(chartDef.slots)) {
    if (slot.kind === 'dimension' || slot.kind === 'time') {
      const val = slotValues[key]
      const dimId = val?.id
      if (dimId && queryInfo) {
        const dimInfo = queryInfo.availableDimensions?.find(d => d.id === dimId)
        parts.push('by ' + (dimInfo?.name || dimInfo?.label || dimId))
        break
      }
    }
  }

  return parts.join(' — ')
}

export default function QueryBuilder({ onSubmit, onCancel, initialConfig = null }) {
  const { state: { queriesInfo } } = useAnalyticsContext()

  const [kind, setKind] = useState(initialConfig?.kind || '')
  const [chartType, setChartType] = useState(initialConfig?.chartType || '')
  const [slotValues, setSlotValues] = useState(() => restoreSlots(initialConfig))
  const [overrideFilters, setOverrideFilters] = useState(initialConfig?.overrideFilters || {})
  const [orderByMetric, setOrderByMetric] = useState(initialConfig?.orderBy?.[0]?.id || '')
  const [orderByDirection, setOrderByDirection] = useState(initialConfig?.orderBy?.[0]?.direction || 'desc')
  const [limit, setLimit] = useState(initialConfig?.limit || '')
  const [name, setName] = useState(initialConfig?.name || '')

  const queryInfo = useMemo(() => queriesInfo.find(q => q.kind === kind), [queriesInfo, kind])
  const chartDef = CHART_TYPES[chartType]
  const isEditingStatic = initialConfig?.static

  const handleKindChange = (newKind) => {
    setKind(newKind)
    if (chartType) {
      const info = queriesInfo.find(q => q.kind === newKind)
      if (info) setSlotValues(CHART_TYPES[chartType].defaults(info))
    }
    setOverrideFilters({})
    setOrderByMetric('')
    setLimit('')
  }

  const handleChartTypeChange = (newType) => {
    setChartType(newType)
    if (queryInfo) {
      setSlotValues(CHART_TYPES[newType].defaults(queryInfo))
    }
    setOrderByMetric('')
  }

  const setSlot = (slotKey, value) => {
    setSlotValues(prev => {
      const next = { ...prev, [slotKey]: value }

      // If a dimension slot changed, clear other dimension slots that now conflict
      if (chartDef && value?.id) {
        for (const [otherKey, otherSlot] of Object.entries(chartDef.slots)) {
          if (otherKey === slotKey || otherSlot.kind !== 'dimension') continue
          const otherVal = next[otherKey]
          if (otherSlot.single && otherVal?.id === value.id) {
            next[otherKey] = null
          } else if (Array.isArray(otherVal)) {
            next[otherKey] = otherVal.filter(d => (d.id || d) !== value.id)
          }
        }
      }

      return next
    })
  }

  const allSelectedMetricIds = chartDef
    ? Object.entries(chartDef.slots)
        .filter(([, slot]) => slot.kind === 'metric')
        .flatMap(([key]) => {
          const val = slotValues[key]
          return Array.isArray(val) ? val : val ? [val] : []
        })
    : []

  const autoName = useMemo(
    () => generateName(kind, chartType, slotValues, queryInfo),
    [kind, chartType, slotValues, queryInfo]
  )

  const isStatic = chartDef?.static

  const handleSubmit = () => {
    if (isStatic) {
      const chartOptions = chartDef.buildChartOptions(slotValues, [])
      onSubmit({
        name: name || autoName,
        chartType,
        chartOptions,
        static: true,
      })
      return
    }

    const { dimensions, metrics, chartOptions } = buildConfig(chartType, slotValues, queryInfo)

    const primaryDimCol = chartOptions.columns?.[0]
    const config = {
      name: name || autoName,
      kind,
      chartType,
      metrics,
      dimensions,
      primaryDimension: primaryDimCol
        ? { id: primaryDimCol.id, label: primaryDimCol.label }
        : { id: 'time', label: 'Time' },
      chartOptions,
      orderBy: orderByMetric
        ? [{ kind: 'metric', id: orderByMetric, direction: orderByDirection }]
        : [],
      limit: limit ? parseInt(limit, 10) : null,
      overrideFilters: _.pickBy(overrideFilters, v => v !== undefined && v !== null),
      ...(slotValues.comparePrevious ? { comparePeriods: 1 } : {}),
    }

    if (dimensions.length > 1) {
      const secDimCol = chartOptions.columns?.find(c => c.id === _.camelCase(dimensions[1].id))
      if (secDimCol) config.secondaryDimension = { id: secDimCol.id, label: secDimCol.label }
    }

    onSubmit(config)
  }

  const slotsValid = chartDef && Object.entries(chartDef.slots).every(([key, slot]) => {
    if (!slot.required) return true
    const val = slotValues[key]
    if (Array.isArray(val)) return val.length > 0
    return val !== null && val !== undefined && val !== ''
  })

  const isValid = chartType && chartDef && (isStatic || kind) && slotsValid

  return (
    <div className="query-builder">
      {!isEditingStatic && (
        <div className="query-builder__section">
          <h3>Visualisation</h3>
          <div className="query-builder__options">
            {Object.entries(CHART_TYPES).map(([id, def]) => (
              <button
                key={id}
                type="button"
                className={`query-builder__option ${chartType === id ? 'active' : ''}`}
                onClick={() => handleChartTypeChange(id)}
              >
                <span>{def.label}</span>
                <span className="query-builder__option-desc">{def.description}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {chartDef && !isStatic && (
        <div className="query-builder__section">
          <h3>Data source</h3>
          <div className="query-builder__options">
            {queriesInfo.map(q => (
              <button
                key={q.kind}
                type="button"
                className={`query-builder__option ${kind === q.kind ? 'active' : ''}`}
                onClick={() => handleKindChange(q.kind)}
              >
                {q.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {chartDef && (isStatic || queryInfo) && (
        <>
          {Object.entries(chartDef.slots).map(([slotKey, slot]) => (
            <SlotEditor
              key={slotKey}
              slot={slot}
              slotKey={slotKey}
              value={slotValues[slotKey]}
              allSlotValues={slotValues}
              allSlots={chartDef.slots}
              onChange={(val) => setSlot(slotKey, val)}
              queryInfo={queryInfo}
            />
          ))}

          {queryInfo?.availableFilters?.length > 0 && (
            <div className="query-builder__section">
              <h3>Filters <span className="query-builder__optional">(optional)</span></h3>
              <Filters
                filters={queryInfo.availableFilters}
                filterValues={overrideFilters}
                setFilterValue={(id, value) => setOverrideFilters(prev => ({ ...prev, [id]: value }))}
                clearFilterValue={(id) => setOverrideFilters(prev => { const next = { ...prev }; delete next[id]; return next })}
              />
            </div>
          )}

          {chartDef.supportsOrderLimit && (
            <div className="query-builder__section">
              <h3>Order & Limit <span className="query-builder__optional">(optional)</span></h3>
              <div className="query-builder__row">
                <select value={orderByMetric} onChange={(e) => setOrderByMetric(e.target.value)}>
                  <option value="">No ordering</option>
                  {allSelectedMetricIds.map(id => {
                    const m = queryInfo.availableMetrics.find(am => am.id === id)
                    return <option key={id} value={id}>{m?.label || id}</option>
                  })}
                </select>
                {orderByMetric && (
                  <select value={orderByDirection} onChange={(e) => setOrderByDirection(e.target.value)}>
                    <option value="desc">Descending</option>
                    <option value="asc">Ascending</option>
                  </select>
                )}
                <input
                  type="number"
                  placeholder="Limit (e.g. 10)"
                  value={limit}
                  onChange={(e) => setLimit(e.target.value)}
                  min="1"
                  style={{ width: 140 }}
                />
              </div>
            </div>
          )}
        </>
      )}

      {chartDef && (isStatic || queryInfo) && (
        <div className="query-builder__section">
          <h3>Name</h3>
          <input
            type="text"
            className="query-builder__name-input"
            placeholder={autoName || 'Query name'}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
      )}

      <div className="query-builder__actions">
        <button type="button" className="query-builder__cancel" onClick={onCancel}>Cancel</button>
        <button type="button" className="query-builder__submit" onClick={handleSubmit} disabled={!isValid}>
          {initialConfig ? 'Update' : 'Add'}
        </button>
      </div>
    </div>
  )
}
